import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

import { WidgetSchema } from '@/entities/widget';

import { Background2 } from '@/shared/assets';

import { additionalWidgets, initialWidgets } from '../lib/constants';
import { isWidgetSchemaAccount } from '../lib/helpers';

interface CustomizationState {
  widgets: WidgetSchema[];
  additionalWidgets: WidgetSchema[];
  background: string;
  editingBackground: string | null;
  accountsBgs: WidgetSchema[];
  totalWidget: string;
  mobileOrder: string[];
}

interface CustomizationActions {
  setWidgets: (widgets: WidgetSchema[]) => void;
  setBackground: (background: string) => void;
  setEditingBackground: (background: string | null) => void;
  setAdditionalWidgets: (widgets: WidgetSchema[]) => void;
  setAccountsBgs: (value: WidgetSchema[]) => void;
  setTotalWidget: (value: string) => void;
  setMobileOrder: (ids: string[]) => void;
  reset: VoidFunction;
}

type CustomizationStore = CustomizationState & CustomizationActions;

const initialState: CustomizationState = {
  widgets: initialWidgets,
  mobileOrder: ['4', '1', '2', '3', '5'],
  additionalWidgets,
  background: Background2,
  editingBackground: null,
  accountsBgs: [],
  totalWidget: 'all',
};

export const useCustomizationStore = create<CustomizationStore>()(
  persist(
    (set) => ({
      ...initialState,
      setWidgets: (widgets) => {
        set({ widgets });
      },
      setTotalWidget: (value) => {
        set({ totalWidget: value });
      },
      setBackground: (background) => {
        set({ background });
      },
      setEditingBackground: (background) => {
        set({ editingBackground: background });
      },
      setAdditionalWidgets: (widgets) => {
        set({ additionalWidgets: widgets });
      },
      setAccountsBgs: (bgs) => {
        set({ accountsBgs: bgs });
      },
      setMobileOrder: (ids) => {
        set({ mobileOrder: ids });
      },
      reset: () => {
        set(initialState);
      },
    }),
    {
      name: 'customizationV2',
      partialize: (state) => ({
        ...state,
        editingBackground: null,
      }),
    },
  ),
);

export const useAccountWidgetSelector = (id?: string | null) => {
  return useShallow((state: CustomizationState) => {
    const widget = state.accountsBgs.find((w) => w.id === id);
    return isWidgetSchemaAccount(widget) ? widget : undefined;
  });
};
